import React, { useCallback, FunctionComponent } from 'react';
import { useTranslation } from '@rbx/intl';
import { Grid, CloseIcon, Drawer, MenuOpenIcon, IconButton } from '@rbx/ui';

import { TestId } from '@modules/utilities/constants/testConstants';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import useSideBarNavigationStyles from './SideBarNavigation.styles';
import SideBarContent from './SideBarContent';
import useSideBarContent from '../hooks/useSideBarContent';

export interface SideBarNavigationProps {
  collapseTextKey?: string;
}

const SideBarNavigation: FunctionComponent<React.PropsWithChildren<SideBarNavigationProps>> = ({
  collapseTextKey = 'Label.CollapseSidebar',
}) => {
  const { translate } = useTranslation();
  const { enableNewLayout } = useAppConfig();
  const { open, setOpen } = useSideBarContent();

  const {
    classes: {
      drawerOpen,
      drawerClosed,
      docked,
      paper,
      toggle,
      toggleClosed,
      navigationBackground,
    },
    cx,
  } = useSideBarNavigationStyles();

  const onToggleSidebar = useCallback(() => {
    setOpen((isOpen) => !isOpen);
  }, [setOpen]);

  const onDrawerClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  if (enableNewLayout) {
    return <SideBarContent />;
  }
  return (
    <Drawer
      open={open}
      data-testid={TestId.Sidebar}
      onClose={onDrawerClose}
      // ensure drawer can be rendered on server side as well, see:
      // https://mui.com/components/modal/#performance & https://mui.com/components/modal/#server-side-modal
      ModalProps={{ keepMounted: true, disablePortal: true }}
      variant='permanent'
      classes={{
        docked,
        paper: cx(paper, navigationBackground, {
          [drawerOpen]: open,
          [drawerClosed]: !open,
        }),
      }}>
      {open && <SideBarContent />}
      <Grid
        item
        onClick={onToggleSidebar}
        className={cx(toggle, {
          [toggleClosed]: !open,
        })}>
        <IconButton aria-label={translate(collapseTextKey)} size='small'>
          {open ? (
            <CloseIcon color='secondary' fontSize='small' />
          ) : (
            <MenuOpenIcon color='secondary' fontSize='small' />
          )}
        </IconButton>
      </Grid>
    </Drawer>
  );
};

export default SideBarNavigation;
